import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@apollo/client";
import { useAuth } from "@ticketon/authentication-core";
import { getTravelAdminClient } from "@/graphql";
import { GET_MY_PERMISSIONS } from "@/graphql/permissionQueries";

interface IPermissionsContext {
  permissions: string[];
  isLoading: boolean;
}

const PermissionsContext = createContext<IPermissionsContext>({
  permissions: [],
  isLoading: true,
});

interface IPermissionsProviderProps {
  children: ReactNode;
}

/**
 * Loads the signed-in user's effective permissions once and shares them with every
 * guard. Access is permission-based only - roles are not consulted anywhere in the
 * client.
 */
export function PermissionsProvider({ children }: IPermissionsProviderProps) {
  const { isAuthenticated } = useAuth();

  const { data, error } = useQuery(GET_MY_PERMISSIONS, {
    client: getTravelAdminClient(),
    skip: !isAuthenticated,
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  const value = useMemo<IPermissionsContext>(() => {
    const fromApi = data?.myPermissions as string[] | undefined;

    if (Array.isArray(fromApi)) {
      return { permissions: fromApi, isLoading: false };
    }

    // No permission list means no access - including when the query fails. Failing open
    // here would hand a user every permission on a transient network error.
    if (error) {
      return { permissions: [], isLoading: false };
    }

    // Anything else means the answer is not in yet: auth is still resolving, or it just
    // resolved and the skipped query has not started (Apollo reports loading: false while
    // skipped). Reporting "not loading" with an empty list in that window is what made the
    // no-access page flash before the permissions arrived.
    return { permissions: [], isLoading: true };
  }, [data, error]);

  return (
    <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
  );
}

export function usePermissionsContext(): IPermissionsContext {
  return useContext(PermissionsContext);
}
